import React from 'react';
import { ArrowRight, CheckCircle2, Phone, Mail } from 'lucide-react';
import { Button } from './ui/button';

const Hero = () => {
  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const scrollToPortfolio = () => {
    const element = document.getElementById('portfolio');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="hero" className="relative min-h-screen flex items-center pt-20 overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 bg-gradient-to-br from-gray-50 via-white to-blue-50/30 -z-10">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-0 left-0 w-full h-full"
            style={{
              backgroundImage: `repeating-linear-gradient(45deg, #4A4A4A 0px, #4A4A4A 2px, transparent 2px, transparent 10px)`,
            }}
          />
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div className="space-y-8 animate-fade-in">
            <div className="inline-flex items-center px-4 py-2 bg-blue-50 rounded-full border border-blue-100">
              <span className="text-[#0070C0] font-semibold text-sm">
                🍁 Trusted Canadian Renovation Experts
              </span>
            </div>

            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold leading-tight">
              Transform Your Space Into{' '}
              <span className="text-[#0070C0] relative inline-block">
                Something Special
                <svg
                  className="absolute -bottom-2 left-0 w-full"
                  height="12"
                  viewBox="0 0 300 12"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M2 10C50 3 150 3 298 10"
                    stroke="#0070C0"
                    strokeWidth="3"
                    strokeLinecap="round"
                  />
                </svg>
              </span>
            </h1>

            <p className="text-xl text-gray-600 leading-relaxed">
              Expert interior renovations for residential and commercial spaces. From custom
              kitchens to legal basements, we bring your vision to life with precision and care.
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                onClick={scrollToContact}
                size="lg"
                className="bg-[#0070C0] hover:bg-[#005A9C] text-white px-8 py-6 text-lg group shadow-lg hover:shadow-xl transition-all"
              >
                Get Free Quote
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button
                onClick={scrollToPortfolio}
                size="lg"
                variant="outline"
                className="border-2 border-gray-300 hover:border-[#0070C0] hover:text-[#0070C0] px-8 py-6 text-lg"
              >
                View Our Work
              </Button>
            </div>

            {/* Trust Indicators */}
            <div className="grid grid-cols-3 gap-6 pt-8 border-t border-gray-200">
              <div>
                <div className="text-3xl font-bold text-[#0070C0]">5+</div>
                <div className="text-sm text-gray-600">Years Experience</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-[#0070C0]">500+</div>
                <div className="text-sm text-gray-600">Projects Done</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-[#0070C0]">100%</div>
                <div className="text-sm text-gray-600">Satisfaction</div>
              </div>
            </div>
          </div>

          {/* Right Content - Feature Cards */}
          <div className="relative">
            <div className="grid grid-cols-2 gap-6">
              {/* Main Image Card */}
              <div className="col-span-2 rounded-2xl overflow-hidden shadow-2xl transform hover:scale-[1.02] transition-transform duration-500">
                <img
                  src="https://customer-assets.emergentagent.com/job_basement-legal/artifacts/en3qs2vh_21710.jpg"
                  alt="Luxury Kitchen Renovation by N-Teek"
                  className="w-full h-80 object-cover"
                />
              </div>

              {/* Feature Card 1 */}
              <div className="bg-white p-6 rounded-xl shadow-lg hover:shadow-2xl transition-shadow border border-gray-100">
                <CheckCircle2 className="w-10 h-10 text-[#0070C0] mb-3" />
                <h3 className="font-bold text-lg mb-2 text-gray-800">Licensed & Insured</h3>
                <p className="text-sm text-gray-600">
                  Fully certified professionals with all necessary permits and insurance
                </p>
              </div>

              {/* Feature Card 2 */}
              <div className="bg-white p-6 rounded-xl shadow-lg hover:shadow-2xl transition-shadow border border-gray-100">
                <CheckCircle2 className="w-10 h-10 text-[#0070C0] mb-3" />
                <h3 className="font-bold text-lg mb-2 text-gray-800">Quality Guarantee</h3>
                <p className="text-sm text-gray-600">
                  Premium materials and craftsmanship backed by our quality promise
                </p>
              </div>
            </div>

            {/* Floating Contact Card */}
            <div className="absolute -bottom-6 -right-6 bg-[#0070C0] text-white p-6 rounded-xl shadow-2xl hidden lg:block">
              <div className="flex items-center gap-3 mb-2">
                <Phone className="w-5 h-5" />
                <span className="font-semibold">Call Us Now</span>
              </div>
              <a href="tel:+14372481943" className="text-2xl font-bold hover:underline block">
                437-248-1943
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
